import { useEffect, useMemo, useState } from 'react';

import InfoButtonSection from './components/InfoButtonSection';
import { PrizeCoverImage } from './components/PrizeCoverImage';
import RankingTable from './components/RankingTable';
import TournamentAlertCard from './components/TournamentAlertCard';
import UserScoreCard from './components/UserScoreCard';

import { Button, Loader, Tabs } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  tdeDateAndGeneralStudentWinners,
  tdeDateSchoolWinnersList,
  tdeMySchool,
  tdeTournamentSchoolWinnerList,
} from '@/services/tde';
import {
  DataTableType,
  ResponseTdeInit,
  ResponseTdeRankingData,
} from '@/types/tde';
import { formatearNumero, GENERICS_WORDS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface SchoolTournamentProps {
  backButtonAction: () => void;
  changeSchoolType: (num: number) => void;
  dataInit: ResponseTdeInit | undefined;
  isLoading: boolean;
  schoolIsLoading: boolean;
  schooldata: ResponseTdeRankingData | undefined;
  pageSelected: number;
}

const initStateResult: DataTableType = {
  studentsData: [],
  schoolsData: [],
  mySchoolsData: [],
  mySchoolDataGeneral: [],
};

export const SchoolTournament = ({
  backButtonAction,
  changeSchoolType,
  dataInit,
  isLoading,
  schoolIsLoading,
  schooldata,
  pageSelected,
}: SchoolTournamentProps) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const { tab: tabRanking, handleTabChanges: handleTabRanking } =
    useHandleChangesTabs();
  const { tab: tabTable, handleTabChanges: handleTabTable } =
    useHandleChangesTabs();
  const [dataTable, setDataTable] = useState(initStateResult);
  const [participantsNum, setParticipantsNum] = useState<number>(0);
  const { userInitialData } = useAuth();

  useEffect(() => {
    setDataTable(initStateResult);
    const fetchData = async () => {
      if (dataInit && tabRanking === 0) {
        if (tabTable === 0) {
          try {
            const response = await tdeDateSchoolWinnersList({
              nivel: pageSelected === 1 ? 'primaria' : 'secundaria',
              idFecha: dataInit.idFecha,
            });
            setParticipantsNum(response.cantEscuelas);
            setDataTable((prev) => ({
              ...prev,
              schoolsData: response.escuela ?? [],
            }));
          } catch (error) {
            console.error(error);
          }
        } else {
          try {
            const response = await tdeTournamentSchoolWinnerList({
              nivel: pageSelected === 1 ? 'primaria' : 'secundaria',
              idFecha: dataInit.idFecha,
            });
            setParticipantsNum(response.cantEscuelas);
            setDataTable((prev) => ({
              ...prev,
              schoolsData: response.escuela ?? [],
            }));
          } catch (error) {
            console.error(error);
          }
        }
      }
      if (dataInit && tabRanking === 1) {
        try {
          const tipo = tabTable === 0 ? 'fecha' : 'gral';
          const response = await tdeDateAndGeneralStudentWinners({
            tipo,
            nivel: pageSelected === 1 ? 'primaria' : 'secundaria',
            idFecha: dataInit.idFecha,
          });
          setParticipantsNum(response.cantParticipantes);
          setDataTable((prev) => ({
            ...prev,
            studentsData: response.usuario ?? [],
          }));
        } catch (error) {
          console.error(error);
        }
      }
      if (schooldata?.idEscuela && dataInit && Number(tabRanking) === 2) {
        try {
          const response = await tdeMySchool({
            idEscuela: schooldata.idEscuela,
            tipoRanking: 0,
            primaria: pageSelected === 1,
          });
          const responseGeneral = await tdeMySchool({
            idEscuela: schooldata.idEscuela,
            tipoRanking: 1,
            primaria: pageSelected === 1,
          });
          setDataTable((prev) => ({
            ...prev,
            mySchoolsData: response.ranking ?? [],
            mySchoolDataGeneral: responseGeneral.ranking ?? [],
          }));
        } catch (error) {
          console.error(error);
        }
      }
    };

    fetchData();
  }, [dataInit, tabRanking, tabTable, schooldata, pageSelected]);

  useEffect(() => {
    if (!schooldata?.idEscuela) {
      handleTabRanking(0);
    } else {
      dataInit && dataInit.participa === 3
        ? handleRankingTabsChanges(0)
        : handleRankingTabsChanges(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit, schooldata?.idEscuela]);

  const rankingTabsData = (): DefaultTabsProps['tabs'] => {
    const baseTabs = [
      { name: 'Ranking de Escuelas' },
      { name: 'Ranking de Chicos' },
    ];
    if (schooldata && 'idEscuela' in schooldata && schooldata.tieneEdad)
      return [...baseTabs, { name: 'Ranking de mi Escuela' }];
    return baseTabs;
  };

  const tableTabsData = (): DefaultTabsProps['tabs'] => {
    if (dataInit) {
      return [
        { name: dataInit.nombreFecha ?? userInitialData?.nombre_fecha ?? '' },
        { name: GENERICS_WORDS.gral },
      ];
    }
    return [];
  };

  const handleRankingTabsChanges = (tab: TabType) => {
    handleTabRanking(tab);
  };

  const handleTableTabsChanges = (tab: TabType) => {
    handleTabTable(tab);
  };

  const changeSchoolText = () => {
    if (pageSelected === 1) {
      return 'Ir a Torneo de Escuelas Secundarias';
    }
    return 'Ir a Torneo de Escuelas Primarias';
  };

  const subtitleText = useMemo(() => {
    return tabRanking === 0
      ? `${formatearNumero(participantsNum)} ESCUELAS PARTICIPANDO`
      : tabRanking === 1
      ? `${formatearNumero(participantsNum)} CHICOS PARTICIPANDO`
      : `-`;
  }, [tabRanking, participantsNum]);

  const getTournamentTitle = () => {
    if (pageSelected === 1) {
      return 'Torneo de Escuelas Primarias';
    }
    return 'Torneo de Escuelas Secundarias';
  };

  const textStyle =
    Number(tabRanking) === 2
      ? 'text-[14px] font-bold text-gdt-gray-7'
      : 'text-[14px] font-bold text-gdt-gray-4';

  const mobileLeftContent = () => {
    if (isMobile && dataInit && dataInit.participa === 3) {
      return {
        backAction: () => {
          backButtonAction();
        },
      };
    }
    return {
      showMenuAction: true,
      items: true,
    };
  };

  const adsConfig = useMemo(() => {
    return pageSelected === 1
      ? SECTION_NAME_FOR_ADS.escuelaprimaria
      : SECTION_NAME_FOR_ADS.escuelasecundaria;
  }, [pageSelected]);

  return (
    <>
      {<Loader isVisible={isLoading && schoolIsLoading} timeout={true} />}
      <Layout
        adsSections={adsConfig}
        backButton={isDesktop && dataInit && dataInit.participa === 3}
        backButtonAction={backButtonAction}
        className="px-4"
        coverTop={() =>
          isDesktop ? (
            <PrizeCoverImage
              isPrimary={pageSelected === 1}
              tabTable={Number(tabTable)}
            />
          ) : undefined
        }
        main={() => (
          <div>
            {isDesktop && (
              <>
                <div className="flex flex-col items-start gap-2 mb-6">
                  <p className="text-[24px] font-bold">
                    {getTournamentTitle()}
                  </p>
                  <p className={textStyle}>{subtitleText}</p>
                </div>
                <div className="flex flex-col items-center mb-8">
                  <Tabs
                    activeTab={tabRanking}
                    className="mt-2 lg:max-w-[515px]"
                    tabs={rankingTabsData()}
                    onClick={handleRankingTabsChanges}
                  />
                </div>
              </>
            )}
            {/* tabs de fecha o torneo */}
            <div className="lg:bg-white lg:rounded-xl">
              <div className="text-center lg:pt-6">
                <Tabs
                  activeTab={tabTable}
                  className="mt-2 lg:max-w-none lg:h-[38px] lg:mt-0 lg:justify-center lg:w-full"
                  classNameTabs="lg:max-w-[200px]"
                  tabs={tableTabsData()}
                  variant="underline"
                  onClick={handleTableTabsChanges}
                />
              </div>
              {schooldata && (
                <RankingTable
                  activeTab={
                    tabRanking === 0
                      ? 'schoolsData'
                      : tabRanking === 1
                      ? 'studentsData'
                      : 'mySchoolsData'
                  }
                  dataTable={dataTable}
                  schooldata={schooldata}
                  tabTable={Number(tabTable)}
                />
              )}
            </div>
          </div>
        )}
        mobile={{
          leftContent: mobileLeftContent(),
          centerContent: {
            title:
              pageSelected === 1
                ? 'Torneo de Escuelas Primarias'
                : 'Torneo de Escuelas Secundarias',
          },
        }}
        sidebar={() =>
          isDesktop ? (
            <div className="flex flex-col gap-y-8 mt-[155px]">
              {schooldata && dataInit && (
                <>
                  <TournamentAlertCard
                    isParticipatingInTournament={!!dataInit.escuelaId}
                    isPrimary={pageSelected === 1}
                    schooldata={schooldata}
                  />
                  <UserScoreCard
                    dataTable={dataTable}
                    isParticipatingInTournament={!!dataInit.escuelaId}
                    schooldata={schooldata}
                    tabRanking={Number(tabRanking)}
                  />
                </>
              )}
              <div className="w-[426px] flex flex-col justify-center items-center">
                <InfoButtonSection isPrimary={pageSelected === 1} />
                <Button
                  className="w-[302px] ml-8"
                  type="button"
                  variant="secondary"
                  onClick={() => changeSchoolType(pageSelected === 1 ? 2 : 1)}
                >
                  {changeSchoolText()}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-y-6 justify-center items-center">
              <Button
                className="w-[302px] mt-6"
                type="button"
                variant="secondary"
                onClick={() => changeSchoolType(pageSelected === 1 ? 2 : 1)}
              >
                {changeSchoolText()}
              </Button>
              <InfoButtonSection isPrimary={pageSelected === 1} />
            </div>
          )
        }
        tabs={() =>
          isMobile ? (
            <div className="flex flex-col items-center">
              <Tabs
                activeTab={tabRanking}
                className="mt-2 w-[calc(100vw-80px)]"
                tabs={rankingTabsData()}
                onClick={handleRankingTabsChanges}
              />
            </div>
          ) : undefined
        }
        trackProps={{
          escuela: dataInit?.nombreEscuela || '',
          primaria: dataInit?.participa === 3,
          secundario: dataInit?.participa === 2,
        }}
      />
    </>
  );
};
